import { BaseEntityModel } from '@/modules/core/app/models/abstracts/BaseEntityModel';
import { ClaraMessageObject } from '@/modules/ai/models/ClaraMessageObject';
import { AskClaraQuestionResponseObject } from '@/modules/ai/models/AskClaraQuestionResponseObject';
import { v4 as uuidv4 } from 'uuid';
import { MessageActor } from '@/modules/ai/constants/MessageActor';
import { EstateRagFeedbackObject } from '@/modules/ai/models/EstateRagFeedbackObject';
import Lang from '@/modules/core/app/services/Lang';
import alertTriangle from '@/assets/illustrations/alert-triangle.svg';
import dayjs from 'dayjs';

const OUTDATED_AFTER_DAYS: number = 10;

export class ClaraConversationObject extends BaseEntityModel {
  id?: number;
  ceUserId?: number;
  ceEstateId?: number;
  title?: string;
  origin?: string;
  isPinned?: boolean;
  messages?: Relationship<ClaraMessageObject[] | null>;
  feedback?: Relationship<EstateRagFeedbackObject | null>;

  constructor(model: Partial<ClaraConversationObject> = {}) {
    super(model);

    this.id = model.id;
    this.ceUserId = model.ceUserId;
    this.ceEstateId = model.ceEstateId;
    this.title = model.title;
    this.origin = model.origin;
    this.isPinned = model.isPinned;
    this.messages = ClaraMessageObject.mapModelsOrNull(model.messages);
    this.feedback = EstateRagFeedbackObject.mapModelOrNull(model.feedback);

    if (!this.title && this.messages?.[0]) {
      this.title = this.messages[0].text;
    }
  }

  get modelName(): string | undefined {
    return this.messages?.[0]?.modelName;
  }

  get hasAnswer(): boolean {
    return !!this.messages?.find((message: ClaraMessageObject) => message.isClaraMessage);
  }

  get hasSuccessfulAnswer(): boolean {
    return !!this.messages?.some((message: ClaraMessageObject) => message.isSuccess);
  }

  get isSavedInHistory(): boolean {
    return !!this.messages?.some((message: ClaraMessageObject) => message.isSavedInHistory);
  }

  get questions(): ClaraMessageObject[] {
    return this.messages?.filter((message: ClaraMessageObject) => message.isUserMessage) ?? [];
  }

  get answers(): ClaraMessageObject[] {
    return this.messages?.filter((message: ClaraMessageObject) => message.isClaraMessage) ?? [];
  }

  get latestQuestion(): ClaraMessageObject {
    return this.questions[this.questions.length - 1];
  }

  get latestAnswer(): ClaraMessageObject | undefined {
    return this.answers?.[this.answers.length - 1];
  }

  get latestMessage(): ClaraMessageObject | undefined {
    return this.messages?.[this.messages.length - 1];
  }

  get latestAnswerWithAlert(): ClaraMessageObject | undefined {
    return this.answers?.findLast((message: ClaraMessageObject) => message.hasAlert);
  }

  get hasMessagesWithAlert(): boolean {
    return !!this.answers?.some((message: ClaraMessageObject) => message.hasAlert);
  }

  get hasMissingConversationFeedback(): boolean {
    return !this.feedback && this.hasSuccessfulAnswer;
  }

  get hasAlert(): boolean {
    return this.hasMessagesWithAlert || this.hasMissingConversationFeedback;
  }

  get hasNonSuccessMessage(): boolean {
    return !!this.messages?.some((message: ClaraMessageObject) => message.hasNonSuccessStatus);
  }

  get alertIcon(): string {
    if (this.hasMessagesWithAlert) {
      return this.latestAnswerWithAlert!.alertIcon;
    }

    if (this.hasMissingConversationFeedback) {
      return alertTriangle;
    }

    return '';
  }

  get alertMessage(): string {
    if (this.hasMessagesWithAlert) {
      return this.latestAnswerWithAlert!.alertMessage;
    }

    if (this.hasMissingConversationFeedback) {
      return Lang.t('ai.awaiting-feedback');
    }

    return '';
  }

  addUserMessage(text: string): void {
    this.messages?.push(
      new ClaraMessageObject({
        text: text,
        identifier: uuidv4(),
        actor: MessageActor.USER
      })
    );
  }

  updateConversation(claraResponseObject: AskClaraQuestionResponseObject): void {
    this.id = claraResponseObject.conversation?.id;
    this.title = claraResponseObject.conversation?.title;
    this.origin = claraResponseObject.conversation?.origin;
    this.isPinned = claraResponseObject.conversation?.isPinned;
    this.messages = this.messages ?? [];
    this.messages.pop();
    claraResponseObject.question && this.messages.push(claraResponseObject.question);
    claraResponseObject.answer && this.messages.push(claraResponseObject.answer);
  }

  // updateConversation() replaces the last message, so a resubmission must end on the question.
  discardLatestAnswer(): void {
    if (this.latestMessage?.isClaraMessage) {
      this.messages?.pop();
    }
  }

  setIsNew(): void {
    this.latestAnswer && (this.latestAnswer.isNew = true);
  }

  isSearchQueryFound(query: string): boolean {
    const searchTerm: string = query.toLowerCase();

    if (this.title?.toLowerCase().includes(searchTerm)) {
      return true;
    }

    if (this.messages?.length) {
      return this.messages.some((message: ClaraMessageObject) => {
        const plainText: string = this.stripHtml(message?.text ?? '');
        return plainText.toLowerCase().includes(searchTerm);
      });
    }

    return false;
  }

  get isOutdated(): boolean {
    if (!this.latestAnswer) {
      return false;
    }

    return dayjs(this.latestAnswer.createdAt).isBefore(
      dayjs().subtract(OUTDATED_AFTER_DAYS, 'day')
    );
  }

  getMatchSnippet(query: string): string | null {
    const searchTerm: string = query.toLowerCase();

    if (this.title?.toLowerCase().includes(searchTerm)) {
      return null;
    }

    if (this.messages?.length) {
      for (const message of this.messages) {
        const rawText: string = message.text ?? '';
        const text: string = this.stripHtml(rawText);
        const lowerText: string = text.toLowerCase();
        const index: number = lowerText.indexOf(searchTerm);

        if (index !== -1) {
          const start: number = Math.max(0, index - 30);
          const end: number = Math.min(text.length, index + searchTerm.length + 30);
          let snippet: string = text.substring(start, end);

          if (start > 0) {
            snippet = '...' + snippet;
          }

          if (end < text.length) {
            snippet = snippet + '...';
          }

          return snippet.trim();
        }
      }
    }

    return null;
  }

  getHighlightedTitle(query: string): string {
    const title: string = this.title ?? '';
    const search: string = query.trim();

    if (!search || search.length < 2) {
      return title;
    }

    const escapedSearch: string = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex: RegExp = new RegExp(`(${escapedSearch})`, 'gi');

    return title.replace(
      regex,
      '<span class="search-highlight has-text-grey-darker px-1 is-radius-medium has-text-weight-semibold">$1</span>'
    );
  }

  getHighlightedSnippet(query: string, snippet: string): string {
    if (!snippet) {
      return '';
    }

    const search: string = query.trim();

    if (!search || search.length < 2) {
      return snippet;
    }

    const escapedSearch: string = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex: RegExp = new RegExp(`(${escapedSearch})`, 'gi');

    return snippet.replace(
      regex,
      '<span class="search-highlight has-text-grey-darker px-1 is-radius-medium has-text-weight-semibold">$1</span>'
    );
  }

  private stripHtml(html: string): string {
    if (!html) {
      return '';
    }

    const cleaned: string = html.replace(/<citation[^>]*>.*?<\/citation>/gi, '');

    const divElement: HTMLDivElement = document.createElement('div');
    divElement.innerHTML = cleaned;

    return divElement.textContent ?? divElement.innerText ?? '';
  }
}
